import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Code,
  Container,
  CopyButton,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import ResponsiveTable from "../components/ResponsiveTable";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCopy, IconKey, IconTrash, IconLock, IconPlug } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createApiKey, deleteApiKey, getApiKeys } from "../api";
import { usePlan } from "../hooks/usePlan";
import { requestUpgrade } from "../Pricing/upgrade";

const PERMISSION_OPTIONS = [
  { value: "ReadOnly", label: "Read Only" },
  { value: "ReadWrite", label: "Read / Write" },
  { value: "Admin", label: "Admin — full tenant access" },
];

function permissionColor(p) {
  if (p === "admin") return "red";
  if (p === "readwrite") return "yellow";
  return "blue";
}

export default function AdminApiKeysPage() {
  const { t } = useTranslation("Veft");
  const [keys, setKeys] = useState(null);
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const { canAccessApi } = usePlan();
  const [newKey, setNewKey] = useState(null);

  const form = useForm({
    initialValues: { name: "", permission: "ReadOnly", rateLimitPerMinute: 100 },
  });

  useEffect(() => {
    getApiKeys().then(setKeys).catch(() => setKeys([]));
  }, []);

  async function handleCreate(values) {
    try {
      const data = await createApiKey(values);
      setNewKey(data);
      setKeys((prev) => [{ ...data, tokenId: undefined }, ...(prev || [])]);
    } catch {
      notifications.show({ title: "Error", message: "Failed to create API key", color: "red" });
    }
  }

  async function handleDelete(id) {
    await deleteApiKey(id);
    setKeys((prev) => prev.filter((k) => k.id !== id));
    notifications.show({ title: "API key deleted" });
  }

  function closeCreateModal() {
    closeCreate();
    setNewKey(null);
    form.reset();
  }

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        <Group gap="xs">
          <IconKey size={22} />
          <Title order={3}>{t("api_keys_title")}</Title>
        </Group>
        <Text size="sm" c="dimmed">
          {t("api_keys_intro")}
        </Text>

        <Modal
          opened={createOpened}
          onClose={closeCreateModal}
          title={newKey ? t("api_keys_created_title") : t("api_keys_create_title")}
          size="lg"
        >
          {newKey ? (
            <Stack>
              <Text>{t("api_keys_one_time_intro")}</Text>
              <Group>
                <TextInput style={{ flex: 1 }} readOnly value={newKey.tokenId} />
                <CopyButton value={newKey.tokenId}>
                  {({ copied, copy }) => (
                    <Button color={copied ? "teal" : "blue"} onClick={copy} leftSection={<IconCopy size={16} />}>
                      {copied ? t("copied") : t("copy")}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text fw={600} size="sm" c="orange">
                {t("api_keys_one_time_warning")}
              </Text>
            </Stack>
          ) : (
            <form onSubmit={form.onSubmit(handleCreate)}>
              <Stack>
                <TextInput
                  label={t("api_keys_name")}
                  placeholder={t("api_keys_name_placeholder")}
                  {...form.getInputProps("name")}
                />
                <Select
                  label={t("api_keys_permission")}
                  data={PERMISSION_OPTIONS}
                  {...form.getInputProps("permission")}
                />
                <NumberInput
                  label={t("api_keys_rate_limit")}
                  min={10}
                  max={10000}
                  {...form.getInputProps("rateLimitPerMinute")}
                />
                <Button type="submit">{t("api_keys_create_button")}</Button>
              </Stack>
            </form>
          )}
        </Modal>

        <Card withBorder p="lg" radius="md">
          <Group justify="space-between" mb="md">
            <Title order={5}>{t("api_keys_list_title")}</Title>
            <Button
              variant="outline"
              size="xs"
              leftSection={canAccessApi ? undefined : <IconLock size={14} />}
              onClick={() =>
                canAccessApi
                  ? openCreate()
                  : requestUpgrade("API access is available on the Pro plan. Please upgrade to create API keys.")
              }
            >
              {t("api_keys_create_button")}
            </Button>
          </Group>

          {!keys ? (
            <Loader size="sm" />
          ) : keys.length === 0 ? (
            <Text size="sm" c="dimmed">
              {t("api_keys_empty")}
            </Text>
          ) : (
            <ResponsiveTable minWidth={640}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("api_keys_col_name")}</Table.Th>
                  <Table.Th>{t("api_keys_col_key")}</Table.Th>
                  <Table.Th>{t("api_keys_col_permission")}</Table.Th>
                  <Table.Th>{t("api_keys_col_last_used")}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {keys.map((k) => (
                  <Table.Tr key={k.id}>
                    <Table.Td>{k.name || t("api_keys_unnamed")}</Table.Td>
                    <Table.Td>
                      <Code fz="xs">...{k.last4}</Code>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="xs" color={permissionColor(k.permission)}>
                        {k.permission}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {k.lastUsed ? new Date(k.lastUsed).toLocaleDateString() : t("api_keys_never")}
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon color="red" variant="subtle" onClick={() => handleDelete(k.id)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </ResponsiveTable>
          )}
        </Card>

        <Card withBorder p="lg" radius="md">
          <Group gap="xs" mb="xs">
            <IconPlug size={18} />
            <Title order={5}>{t("api_keys_mcp_title")}</Title>
          </Group>
          <Text size="sm" c="dimmed" mb="sm">
            {t("api_keys_mcp_intro")}
          </Text>
          <Group gap="xs">
            <Code fz="sm">{`${window.location.origin}/api/mcp`}</Code>
            <CopyButton value={`${window.location.origin}/api/mcp`}>
              {({ copied, copy }) => (
                <ActionIcon variant="subtle" color={copied ? "teal" : "gray"} onClick={copy}>
                  <IconCopy size={16} />
                </ActionIcon>
              )}
            </CopyButton>
          </Group>
          <Text size="sm" c="dimmed" mt="sm">
            {t("api_keys_mcp_auth_hint")} <Code fz="xs">{"Authorization: Bearer <api-key>"}</Code>
          </Text>
        </Card>
      </Stack>
    </Container>
  );
}
